import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FiUsers, FiTrendingUp, FiPlus, FiEye, FiArrowRight } from 'react-icons/fi';
import { FaCalendarAlt, FaUserTie } from 'react-icons/fa';

const DashboardContainer = styled.div`
  padding: 0;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  
  /* Responsive container for different desktop resolutions */
  @media (min-width: 1920px) {
    max-width: 1440px;
    padding: 0 2.5rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    max-width: 1366px;
    padding: 0 2rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    max-width: 1280px;
    padding: 0 1.75rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    max-width: 1200px;
    padding: 0 1.5rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    max-width: 1024px;
    padding: 0 1.25rem;
  }

  @media (max-width: 1023px) {
    max-width: 100%;
    padding: 0 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: clamp(1.5rem, 3vw, 2rem);
  
  h1 {
    font-size: clamp(1.5rem, 2.5vw, 1.875rem);
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 0.375rem;
    letter-spacing: -0.025em;
  }
  
  p {
    color: #718096;
    font-size: clamp(0.875rem, 1.25vw, 1rem);
    font-weight: 400;
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    margin-bottom: 2rem;
    
    h1 {
      font-size: 1.875rem;
    }
    
    p {
      font-size: 1rem;
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    margin-bottom: 1.875rem;
    
    h1 {
      font-size: 1.75rem;
    }
    
    p {
      font-size: 0.9375rem;
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    margin-bottom: 1.75rem;
    
    h1 {
      font-size: 1.625rem;
    }
    
    p {
      font-size: 0.875rem;
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    margin-bottom: 1.625rem;
    
    h1 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 0.8125rem;
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    margin-bottom: 1.5rem;
    
    h1 {
      font-size: 1.375rem;
    }
    
    p {
      font-size: 0.75rem;
    }
  }

  @media (max-width: 1023px) {
    margin-bottom: 1.5rem;
    
    h1 {
      font-size: 1.375rem;
    }
    
    p {
      font-size: 0.6875rem;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: clamp(0.75rem, 1.5vw, 1.25rem);
  margin-bottom: clamp(1.5rem, 3vw, 2rem);

  /* Responsive grid for different desktop resolutions */
  @media (min-width: 1920px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 1.25rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 1.125rem;
    margin-bottom: 1.875rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 1rem;
    margin-bottom: 1.75rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 0.875rem;
    margin-bottom: 1.625rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: clamp(0.75rem, 1.5vw, 1rem);
  padding: clamp(1.25rem, 2.5vw, 1.5rem);
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  min-height: clamp(8rem, 12vw, 9rem);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.gradient || 'linear-gradient(135deg, #667eea, #764ba2)'};
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
    border-color: #cbd5e0;
  }
  
  .icon {
    width: clamp(2rem, 3vw, 2.5rem);
    height: clamp(2rem, 3vw, 2.5rem);
    border-radius: clamp(0.5rem, 1vw, 0.75rem);
    background: ${props => props.iconBg || 'linear-gradient(135deg, #667eea, #764ba2)'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: clamp(0.875rem, 1.25vw, 1.125rem);
    margin-bottom: clamp(0.75rem, 1.5vw, 1rem);
    box-shadow: 0 8px 16px ${props => props.iconShadow || 'rgba(102, 126, 234, 0.3)'};
  }
  
  .value {
    font-size: clamp(1.25rem, 2.5vw, 1.75rem);
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 0.375rem;
    letter-spacing: -0.025em;
  }
  
  .label {
    color: #718096;
    font-size: clamp(0.75rem, 1vw, 0.875rem);
    font-weight: 500;
    letter-spacing: 0.025em;
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 1.5rem;
    min-height: 9rem;

    .icon {
      width: 2.5rem;
      height: 2.5rem;
      font-size: 1.125rem;
    }
    
    .value {
      font-size: 1.75rem;
    }
    
    .label {
      font-size: 0.875rem;
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 1.375rem;
    min-height: 8.5rem;

    .icon {
      width: 2.25rem;
      height: 2.25rem;
      font-size: 1rem;
    }
    
    .value {
      font-size: 1.625rem;
    }
    
    .label {
      font-size: 0.8125rem;
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 1.25rem;
    min-height: 8rem;

    .icon {
      width: 2rem;
      height: 2rem;
      font-size: 0.9375rem;
    }
    
    .value {
      font-size: 1.5rem;
    }
    
    .label {
      font-size: 0.75rem;
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 1.125rem;
    min-height: 7.5rem;

    .icon {
      width: 1.875rem;
      height: 1.875rem;
      font-size: 0.875rem;
    }
    
    .value {
      font-size: 1.375rem;
    }
    
    .label {
      font-size: 0.6875rem;
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 1rem;
    min-height: 7rem;

    .icon {
      width: 1.75rem;
      height: 1.75rem;
      font-size: 0.8125rem;
    }
    
    .value {
      font-size: 1.25rem;
    }
    
    .label {
      font-size: 0.625rem;
    }
  }

  @media (max-width: 1023px) {
    padding: 1rem;
    min-height: 6.5rem;

    .icon {
      width: 1.625rem;
      height: 1.625rem;
      font-size: 0.75rem;
    }
    
    .value {
      font-size: 1.125rem;
    }
    
    .label {
      font-size: 0.5625rem;
    }
  }
  
  @media (max-width: 768px) {
    min-height: 6rem;
    padding: 0.875rem;
    
    .icon {
      width: 1.5rem;
      height: 1.5rem;
      font-size: 0.6875rem;
    }
    
    .value {
      font-size: 1rem;
    }
    
    .label {
      font-size: 0.5rem;
    }
  }
`;

const ContentGrid = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const QuickActionsCard = styled.div`
  background: white;
  border-radius: clamp(0.75rem, 1.5vw, 1rem);
  padding: clamp(1.25rem, 2.5vw, 1.5rem);
  border: 1px solid #e2e8f0;
  
  .header {
    margin-bottom: clamp(1.25rem, 2.5vw, 1.5rem);
    
    h3 {
      font-size: clamp(1rem, 1.25vw, 1.125rem);
      font-weight: 700;
      color: #1a202c;
      letter-spacing: -0.025em;
    }
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 1.5rem;
    border-radius: 1rem;
    
    .header {
      margin-bottom: 1.5rem;
      
      h3 {
        font-size: 1.125rem;
      }
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 1.375rem;
    border-radius: 0.9375rem;
    
    .header {
      margin-bottom: 1.375rem;
      
      h3 {
        font-size: 1rem;
      }
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 1.25rem;
    border-radius: 0.875rem;
    
    .header {
      margin-bottom: 1.25rem;
      
      h3 {
        font-size: 0.9375rem;
      }
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 1.125rem;
    border-radius: 0.8125rem;
    
    .header {
      margin-bottom: 1.125rem;
      
      h3 {
        font-size: 0.875rem;
      }
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 1rem;
    border-radius: 0.75rem;
    
    .header {
      margin-bottom: 1rem;
      
      h3 {
        font-size: 0.8125rem;
      }
    }
  }

  @media (max-width: 1023px) {
    padding: 1rem;
    border-radius: 0.75rem;
    
    .header {
      margin-bottom: 1rem;
      
      h3 {
        font-size: 0.8125rem;
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    border-radius: 0.625rem;
    
    .header {
      margin-bottom: 0.875rem;
      
      h3 {
        font-size: 0.75rem;
      }
    }
  }
`;

const ActionsList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(1rem, 2vw, 1.5rem);
  
  /* Responsive grid for different desktop resolutions */
  @media (min-width: 1920px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.375rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.125rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(0.75rem, 1.5vw, 1.25rem);
  text-decoration: none;
  gap: clamp(0.5rem, 1vw, 0.75rem);
  background: ${props => props.background || '#f7fafc'};
  border: 1px solid ${props => props.borderColor || '#e2e8f0'};
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.textColor || '#2d3748'};
  font-weight: 500;
  font-size: clamp(0.75rem, 1vw, 0.875rem);
  min-height: clamp(3rem, 5vw, 4rem);
  
  &:hover {
    background: ${props => props.hoverBg || '#edf2f7'};
    border-color: ${props => props.hoverBorder || '#cbd5e0'};
    transform: translateY(-2px);
  }
  
  .left {
    display: flex;
    align-items: center;
    gap: clamp(0.375rem, 0.75vw, 0.5rem);
    flex: 1;
    
    .icon {
      width: clamp(1.25rem, 2vw, 1.75rem);
      height: clamp(1.25rem, 2vw, 1.75rem);
      border-radius: clamp(0.375rem, 0.75vw, 0.5rem);
      background: ${props => props.iconBg || 'linear-gradient(135deg, #667eea, #764ba2)'};
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: clamp(0.625rem, 0.875vw, 0.75rem);
      flex-shrink: 0;
    }
  }
  
  .arrow {
    color: #a0aec0;
    font-size: clamp(0.625rem, 0.875vw, 0.75rem);
    flex-shrink: 0;
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 1.25rem;
    gap: 0.75rem;
    border-radius: 0.75rem;
    min-height: 4rem;
    font-size: 0.875rem;

    .left {
      gap: 0.5rem;
      
      .icon {
        width: 1.75rem;
        height: 1.75rem;
        border-radius: 0.5rem;
      }
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 1.125rem;
    gap: 0.6875rem;
    border-radius: 0.6875rem;
    min-height: 3.75rem;
    font-size: 0.8125rem;

    .left {
      gap: 0.4375rem;
      
      .icon {
        width: 1.625rem;
        height: 1.625rem;
        border-radius: 0.4375rem;
      }
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 1rem;
    gap: 0.625rem;
    border-radius: 0.625rem;
    min-height: 3.5rem;
    font-size: 0.75rem;

    .left {
      gap: 0.375rem;
      
      .icon {
        width: 1.5rem;
        height: 1.5rem;
        border-radius: 0.375rem;
      }
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.875rem;
    gap: 0.5625rem;
    border-radius: 0.5625rem;
    min-height: 3.25rem;
    font-size: 0.6875rem;

    .left {
      gap: 0.3125rem;
      
      .icon {
        width: 1.375rem;
        height: 1.375rem;
        border-radius: 0.3125rem;
      }
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.75rem;
    gap: 0.5rem;
    border-radius: 0.5rem;
    min-height: 3rem;
    font-size: 0.625rem;

    .left {
      gap: 0.25rem;
      
      .icon {
        width: 1.25rem;
        height: 1.25rem;
        border-radius: 0.25rem;
      }
    }
  }

  @media (max-width: 1023px) {
    padding: 0.75rem;
    gap: 0.5rem;
    border-radius: 0.5rem;
    min-height: 2.75rem;
    font-size: 0.625rem;

    .left {
      gap: 0.25rem;
      
      .icon {
        width: 1.125rem;
        height: 1.125rem;
        border-radius: 0.25rem;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 0.625rem;
    gap: 0.375rem;
    border-radius: 0.375rem;
    min-height: 2.5rem;
    font-size: 0.5625rem;

    .left {
      gap: 0.1875rem;
      
      .icon {
        width: 1rem;
        height: 1rem;
        border-radius: 0.1875rem;
      }
    }
    
    .arrow {
      font-size: 0.625rem;
    }
  }
`;

const Dashboard = ({ users, leads, currentUser }) => {
  // Calculate real statistics from backend data
  const activeUsers = users.filter(user => user.isActive).length;
  const totalLeads = leads.length;
  const assignedLeads = leads.filter(lead => lead.owner_id !== null).length;
  const unassignedLeads = leads.filter(lead => lead.owner_id === null).length;

  // Calculate leads in last 30 days and 7 days
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
  const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
  // Calculate leads for today
  let today = new Date().toISOString().split('T')[0] + 'T00:00:00';
  let todayLeads = leads.filter(lead => {
    let todayDate = new Date(today);
    const leadDate = new Date(lead.created_at);

    return leadDate >= todayDate;
  });

  const leadsLast30Days = leads.filter(lead => {
    const leadDate = new Date(lead.created_at);
    return leadDate >= thirtyDaysAgo;
  }).length;

  const leadsLast7Days = leads.filter(lead => {
    const leadDate = new Date(lead.created_at);
    return leadDate >= sevenDaysAgo;
  }).length;

  const leadsToday = todayLeads.length;

  // Check if user is admin to determine which stats to show
  const isAdmin = currentUser?.isAdmin || currentUser?.role === 'admin';

  const quickActions = [
    {
      icon: <FiPlus />,
      label: 'Manage Leads',
      href: '/dashboard/leads',
      background: 'linear-gradient(135deg, #e6fffa, #c6f6d5)',
      borderColor: 'rgba(72, 187, 120, 0.2)',
      iconBg: 'linear-gradient(135deg, #48bb78, #38a169)',
      hoverBg: 'linear-gradient(135deg, #c6f6d5, #9ae6b4)'
    },
    ...(isAdmin ? [{
      icon: <FiUsers />,
      label: 'Manage Users & Roles',
      href: '/dashboard/users',
      background: 'linear-gradient(135deg, #ebf8ff, #bee3f8)',
      borderColor: 'rgba(66, 153, 225, 0.2)',
      iconBg: 'linear-gradient(135deg, #4299e1, #3182ce)',
      hoverBg: 'linear-gradient(135deg, #bee3f8, #90cdf4)'
    }] : []),
    {
      icon: <FiEye />,
      label: 'Read Integrations',
      href: '/dashboard/integrations',
      background: 'linear-gradient(135deg, #fef5e7, #fed7aa)',
      borderColor: 'rgba(237, 137, 54, 0.2)',
      iconBg: 'linear-gradient(135deg, #ed8936, #dd6b20)',
      hoverBg: 'linear-gradient(135deg, #fed7aa, #fbd38d)'
    }
  ];

  const statCards = [
    {
      icon: <FiTrendingUp />,
      value: totalLeads,
      label: 'Total Leads',
      gradient: 'linear-gradient(135deg, #f093fb, #f5576c)',
      iconBg: 'linear-gradient(135deg, #f093fb, #f5576c)',
      iconShadow: 'rgba(245, 87, 108, 0.3)'
    },
    {
      icon: <FaCalendarAlt />,
      value: leadsLast30Days,
      label: 'Total in Last 30 Days',
      gradient: 'linear-gradient(135deg, #ffecd2,#36bb58)',
      iconBg: 'linear-gradient(135deg, #ffecd2, #36bb58)',
      iconShadow: 'rgba(255, 236, 210, 0.3)'
    },
    {
      icon: <FaCalendarAlt />,
      value: leadsLast7Days,
      label: 'Total Leads in Last 7 Days',
      gradient: 'linear-gradient(135deg, #f093fb,#1d1b8a)',
      iconBg: 'linear-gradient(135deg, #f093fb, #1d1b8a)',
      iconShadow: 'rgba(168, 237, 234, 0.3)'
    },
    {
      icon: <FaCalendarAlt />,
      value: leadsToday,
      label: 'Total Leads Today',
      gradient: 'linear-gradient(135deg, #f093fb, #f5576c)',
      iconBg: 'linear-gradient(135deg, #f093fb, #f5576c)',
      iconShadow: 'rgba(168, 237, 234, 0.3)'
    },
    {
      icon: <FaUserTie />,
      value: unassignedLeads,
      label: 'Total Unassigned Leads',
      gradient: 'linear-gradient(135deg, #43e97b, #38f9d7)',
      iconBg: 'linear-gradient(135deg, #43e97b, #38f9d7)',
      iconShadow: 'rgba(67, 233, 123, 0.3)'
    },
    // {
    //   icon: <FiUserCheck />,
    //   value: assignedLeads,
    //   label: 'Total Assigned Leads',
    //   gradient: 'linear-gradient(135deg, #4facfe, #00f2fe)',
    //   iconBg: 'linear-gradient(135deg, #4facfe, #00f2fe)',
    //   iconShadow: 'rgba(79, 172, 254, 0.3)'
    // },
    // ...(isAdmin ? [{
    //   icon: <FiUsers />,
    //   value: activeUsers,
    //   label: 'Active Users',
    //   gradient: 'linear-gradient(135deg, #667eea, #764ba2)',
    //   iconBg: 'linear-gradient(135deg, #667eea, #764ba2)',
    //   iconShadow: 'rgba(102, 126, 234, 0.3)'
    // }] : [])
  ];

  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser?.isAdmin) {
      navigate('/dashboard/leads');
    }
  }, [currentUser]);

  return (
    <DashboardContainer>
      <Header>
        <h1>Welcome back! 👋</h1>
        <p>Here's what's happening with your travel business today.</p>
      </Header>

      <StatsGrid>
        {statCards.map((stat, index) => (
          <StatCard
            key={index}
            gradient={stat.gradient}
            iconBg={stat.iconBg}
            iconShadow={stat.iconShadow}
          >
            <div className="icon">{stat.icon}</div>
            <div className="value">{stat.value}</div>
            <div className="label">{stat.label}</div>
          </StatCard>
        ))}
      </StatsGrid>

      <ContentGrid>
        <QuickActionsCard>
          <div className="header">
            <h3>Quick Actions</h3>
          </div>
          <ActionsList>
            {quickActions.map((action, index) => (
              <ActionButton
                key={index}
                background={action.background}
                borderColor={action.borderColor}
                iconBg={action.iconBg}
                hoverBg={action.hoverBg}
                to={action.href}
              >
                <div className="left">
                  <div className="icon">{action.icon}</div>
                  <span>{action.label}</span>
                </div>
                <FiArrowRight className="arrow" />
              </ActionButton>
            ))}
          </ActionsList>
        </QuickActionsCard>
      </ContentGrid>
    </DashboardContainer>
  );
};

export default Dashboard; 